/**
 * How a model is described in words and bars (web.md §8 fix 6, models.md §12): everything comes from the
 * catalogue the engine ships (`models.catalog()`, `ModelCatalogEntry.quality`) — measured numbers, never
 * invented ones — and from this device's install state.
 *
 * The bars are relative, like the Handy model list the owner referenced (06b): accuracy = the topic-ranking
 * AUC; speed = a 0..1 position on a scale where 0 s is full and 2 s is empty.
 */

import type { Model, ModelCatalogEntry, ModelInstallState, ModelWaitReason } from '@digester/core';

const SPEED_SCALE_SECONDS = 2;

export const ASSISTANT = {
  id: 'assistant',
  name: 'Message assessment',
  subtitle: 'Connected assistant · over MCP',
} as const;

/** "0.94" — two decimals of an AUC. */
export function aucText(auc: number): string {
  return auc.toFixed(2);
}

/** "≈ 0.35 s per message". */
export function speedText(entry: ModelCatalogEntry): string {
  return `≈ ${entry.quality.secondsPerMessage} s per message`;
}

/** The list line of fix 6: "Topic ranking 0.94 · ≈ 0.35 s per message". */
export function qualityLine(entry: ModelCatalogEntry): string {
  return `Topic ranking ${aucText(entry.quality.topicAuc)} · ${speedText(entry)}`;
}

/** "135 MB" (decimal megabytes, as the download size is shown before a download). */
export function megabytes(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  return `${bytes > 0 ? Math.max(1, Math.round(bytes / 1e6)) : 0} MB`;
}

/** Where the model runs: "On this device · 135 MB" / "Through Digester’s server". */
export function whereLine(entry: ModelCatalogEntry): string {
  return entry.kind === 'onnx-embedding' ? `On this device · ${megabytes(entry.downloadBytes)}` : 'Through Digester’s server';
}

export function accuracyValue(entry: ModelCatalogEntry): number {
  return Math.max(0, Math.min(1, entry.quality.topicAuc));
}

export function speedValue(entry: ModelCatalogEntry): number {
  return Math.max(0, Math.min(1, 1 - entry.quality.secondsPerMessage / SPEED_SCALE_SECONDS));
}

/** Percent of a download, 0..100, rounded down (never 100 before the files are verified). */
export function percent(state: ModelInstallState): number {
  if (state.bytesTotal <= 0) return 0;
  return Math.max(0, Math.min(100, Math.floor((state.bytesDone / state.bytesTotal) * 100)));
}

export type ModelStatusTone = 'on' | 'off' | 'warn' | 'error';

export interface ModelStatus {
  /** Active · Off · Pro · Not downloaded · Downloading 43 % · Paused · Verifying · Waiting for download · Download failed … */
  label: string;
  tone: ModelStatusTone;
}

/**
 * A model's status in the list and in its editor's header (models.md §12). `install` is this device's
 * state of an on-device model (undefined while unknown); `unavailable` says why it cannot be read;
 * `wait` is the pipeline's reason the model's messages wait (a remote model that is not answering).
 */
export function modelStatus(model: Model, entry: ModelCatalogEntry | undefined, install: ModelInstallState | undefined, unavailable: string | null, wait: ModelWaitReason | null = null): ModelStatus {
  if (!model.entitled) return { label: 'Pro', tone: 'off' };
  if (entry?.kind !== 'onnx-embedding') {
    if (model.enabled && wait === 'unavailable') return { label: 'Not answering', tone: 'error' };
    return model.enabled ? { label: 'Active', tone: 'on' } : { label: 'Off', tone: 'off' };
  }
  if (unavailable !== null && !install) return model.enabled ? { label: 'Not available here', tone: 'warn' } : { label: 'Off', tone: 'off' };
  switch (install?.phase) {
    case 'downloading':
      return { label: `Downloading ${percent(install)} %`, tone: 'warn' };
    case 'paused':
      return { label: `Paused at ${percent(install)} %`, tone: 'warn' };
    case 'verifying':
      return { label: 'Verifying', tone: 'warn' };
    case 'error':
      return { label: cannotStart(install) ? 'Could not start' : 'Download failed', tone: 'error' };
    case 'unsupported':
      return model.enabled ? { label: 'Not available here', tone: 'warn' } : { label: 'Off', tone: 'off' };
    case 'installed':
      return model.enabled ? { label: 'Active', tone: 'on' } : { label: 'Off', tone: 'off' };
    case 'absent':
      return model.enabled ? { label: 'Waiting for download', tone: 'warn' } : { label: 'Not downloaded', tone: 'off' };
    default:
      return model.enabled ? { label: 'Active', tone: 'on' } : { label: 'Off', tone: 'off' };
  }
}

/**
 * The files are installed but the model's session could not be created on this device (memory, an old
 * browser): phase 'error', code 'runtime', every byte present (review M-R2). Retry tries the session again.
 */
export function cannotStart(state: ModelInstallState): boolean {
  return state.phase === 'error' && state.error?.code === 'runtime' && state.bytesTotal > 0 && state.bytesDone >= state.bytesTotal;
}

/** What an install error means, in words. */
export function installErrorText(state: ModelInstallState): string {
  const e = state.error;
  if (!e) return 'The download stopped.';
  switch (e.code) {
    case 'network':
      return `The download was interrupted (${e.message}). Retry continues where it stopped.`;
    case 'integrity':
      return 'A downloaded file did not match its checksum and was deleted. Retry downloads it again.';
    case 'quota':
      return `Not enough storage space in this browser for the model (${e.message}).`;
    case 'storage':
      return `The browser refused to store the model files (${e.message}).`;
    case 'runtime':
      return cannotStart(state)
        ? `The model is downloaded but could not be started on this device (${e.message}). Messages wait for it; Retry tries again.`
        : `The model could not be started on this device (${e.message}).`;
  }
}

/**
 * Why a pending score is not being produced right now, in words (models.md §12 "Timeline"), for the
 * model named `name` (the on-device model is "the on-device model").
 */
export function waitText(reason: ModelWaitReason | null, name: string, onDevice: boolean): string {
  const who = onDevice ? 'the on-device model' : name;
  switch (reason) {
    case 'download':
      return 'Waiting for the on-device model to download';
    case 'error':
      return 'Waiting for the on-device model — it stopped with an error on this device';
    case 'device':
      return 'Waiting for a device that has the on-device model';
    case 'budget':
      return `Waiting for ${name} — today’s budget is used; continues tomorrow`;
    case 'offline':
      return `Waiting for ${name} — offline`;
    case 'unavailable':
      return `Waiting for ${name} — the scoring service is not answering; trying again`;
    case 'plan':
      return `Waiting for ${name} — not included in your plan`;
    case 'power':
      return 'Paused to save battery';
    case null:
      return `Waiting for ${who}`;
  }
}
